import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from "material-react-table";
import { Button } from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import apiServices from "../../../service";
import OverlayLoader from "../../../components/OverlayLoader";
import SavedSearchDialog from "../../client/SavedSearches";
import {
  fetchExtracts,
  selectConditionExtracts,
  selectSelectionExtracts,
} from "../../../redux/savedSearchSlices/extractSlice";
import { AppDispatch } from "../../../redux/store";

// Define the type for a client row
interface ClientRow {
  customer_id: string;
  first_names: string;
  last_name: string;
  email: string;
}

interface ClientSelectionProps {
  selectionMode?: 'radio' | 'checkbox';
  handleClientSelection?: (ids: string[]) => void;
  setClientList?: (clients: ClientRow[]) => void;
  initialSelectedIds?: string[];
  [key: string]: any;
}

function ClientSelection(props: ClientSelectionProps) {
  const { selectionMode = 'checkbox', initialSelectedIds = [] } = props;
  const dispatch = useDispatch<AppDispatch>();
  const conditionExtracts = useSelector(selectConditionExtracts);
  const selectionExtracts = useSelector(selectSelectionExtracts);

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(
    () => Object.fromEntries(initialSelectedIds.map((id) => [id, true]))
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [savedSearchOpen, setSavedSearchOpen] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState<{
    title: string;
    ids: string[];
    type: "condition" | "selection";
  } | null>(null);
  // Accumulates every client row ever seen across pagination/search changes so that
  // selections made on earlier pages are still resolvable after later pages replace `clients`.
  const seenClientsRef = useRef<Map<string, ClientRow>>(new Map());

  const columns = useMemo<MRT_ColumnDef<ClientRow>[]>(
    () => [
      {
        accessorKey: "first_names",
        header: "First Name",
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
    ],
    []
  );

  // Only condition searches should re-trigger getClientList; selection searches use a separate path
  const conditionIdsKey = selectedSearch?.type === "condition"
    ? (selectedSearch.ids || []).join(",")
    : "";

  const selectionIdsKey = selectedSearch?.type === "selection"
    ? (selectedSearch.ids || []).join(",")
    : "";

  useEffect(() => {
    getClientList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, searchText, conditionIdsKey, sorting]);

  useEffect(() => {
    if (!selectionIdsKey) return;
    getSelectionClientList(selectedSearch!.ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionIdsKey]);

  useEffect(() => {
    dispatch(fetchExtracts());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function recordClients(transformedData: ClientRow[]) {
    transformedData.forEach((client) =>
      seenClientsRef.current.set(client.customer_id, client)
    );
    props.setClientList?.(Array.from(seenClientsRef.current.values()));
  }

  // Selection-type saved searches carry an explicit list of customer ids and are
  // resolved directly via the dedicated customer-lookup endpoint, bypassing the
  // paginated/searchable listing entirely (which has no notion of an id-set filter).
  async function getSelectionClientList(customerIds: string[]) {
    setLoading(true);
    try {
      const resp = await apiServices.exportExcel.clientList(customerIds);
      const raw: any[] = resp?.data?.data ?? [];
      const transformedData: ClientRow[] = raw.map((client: any) => ({
        customer_id: client.CustomerId ?? client.customer_id,
        first_names: client.FirstNames ?? client.first_names ?? "",
        last_name: client.LastName ?? client.last_name ?? "",
        email: client.Email ?? client.email ?? "",
      }));

      setClients(transformedData);
      setRowCount(resp?.data?.count ?? transformedData.length);
      recordClients(transformedData);
    } catch (error) {
      setClients([]);
      setRowCount(0);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function getClientList() {
    if (selectedSearch?.type === 'selection') {
      return getSelectionClientList(selectedSearch.ids || []);
    }

    setLoading(true);
    try {
      const activeSort = sorting[0];
      const resp = await apiServices.client.getClientLists(
        pagination.pageIndex + 1,
        pagination.pageSize,
        searchText,
        undefined,
        undefined,
        selectedSearch?.type === "condition" ? selectedSearch.ids.join(",") : undefined,
        undefined,
        activeSort?.id,
        activeSort ? (activeSort.desc ? "desc" : "asc") : undefined
      );
      // const response = await apiServices.notification.clientList(
      //   pagination.pageIndex + 1,
      //   pagination.pageSize,
      //   searchText
      // );
      if (
        resp &&
        resp?.data &&
        resp?.data?.data &&
        resp?.data?.data?.customerList &&
        resp?.data?.data?.customerList?.length > 0
      ) {
        const transformedData = resp?.data?.data?.customerList?.map(
          (client: any) => ({
            customer_id: client.customer_id,
            first_names: client?.first_names || "",
            last_name: client?.last_name || "",
          })
        );

        setClients(transformedData);
        setRowCount(
          resp.data.data && resp.data.data.count && resp.data.data.count
            ? resp.data.data.count
            : 0
        );
        recordClients(transformedData);
        setLoading(false);
      } else {
        setLoading(false);
        setClients([]);
        setRowCount(0);
      }

      // if (response && response.data) {

      //   setClients(response.data.data ? response.data.data.res : []);
      //   setRowCount(response.data.data ? response.data.data.count : 0);
      // }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }

  const handleSavedSearchApply = (data: {
    conditionIds: string[];
    selectionIds?: string[];
    selectionId: string | null;
    encodedCondition?: string | null;
  }) => {
    if (data.conditionIds.length > 0) {
      const extract = conditionExtracts.find((e: any) => e.SearchId === data.conditionIds[0]);
      setSelectedSearch({ title: extract?.Title || data.conditionIds[0], ids: data.conditionIds, type: "condition" });
    } else if (data.selectionId) {
      const extract = selectionExtracts.find((e: any) => e.SearchId === data.selectionId);
      setSelectedSearch({ title: extract?.Title || data.selectionId, ids: data.selectionIds || [], type: "selection" });
    }
    setSavedSearchOpen(false);
  };

  const handleRowSelectionChange: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>> = (updater) => {
    const newSelection = typeof updater === "function" ? updater(rowSelection) : updater;
    setRowSelection(newSelection);
    props.handleClientSelection?.(Object.keys(newSelection));
  };
  return (
    <div>
      <OverlayLoader isLoading={loading} />

      <div className="table-in-popup">
        <MaterialReactTable<ClientRow>
          columns={columns}
          data={clients}
          onRowSelectionChange={handleRowSelectionChange}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={rowCount}
          manualPagination
          manualSorting
          enableMultiSort={false}
          state={{ pagination, rowSelection, sorting }}
          enableGlobalFilter={true}
          enableColumnActions={false}
          enableColumnFilters={true}
          enableRowSelection
          muiTableBodyCellProps={({ column }) => ({
            style: {
              padding: "6px 12px",
              fontSize: "0.875rem",
            },
          })}
          enableFullScreenToggle={false}
          getRowId={(row) => row.customer_id}
          enableMultiRowSelection={selectionMode !== 'radio'}
          muiSelectCheckboxProps={(selectionMode === 'radio' ? { type: 'radio' } : {}) as any}
          muiTablePaginationProps={{
            showFirstButton: false,
            showLastButton: false,
          }}
          onGlobalFilterChange={(text: string) => {
            if (text) {
              setSearchText(text);
            } else {
              setSearchText("");
            }
          }}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                shape="rounded"
                appearance={selectedSearch ? "primary" : "outline"}
                className={selectedSearch ? "asc-button-primary" : ""}
                onClick={() => setSavedSearchOpen(true)}
              >
                {selectedSearch ? `Search: ${selectedSearch.title}` : "Saved Search"}
              </Button>
              {selectedSearch && (
                <Button
                  appearance="subtle"
                  shape="circular"
                  style={{ minWidth: "auto" }}
                  onClick={() => setSelectedSearch(null)}
                >
                  ✕
                </Button>
              )}
            </div>
          )}
        />
      </div>

      <SavedSearchDialog
        open={savedSearchOpen}
        onClose={() => setSavedSearchOpen(false)}
        onApply={handleSavedSearchApply}
        onClear={() => setSelectedSearch(null)}
        initialConditionIds={selectedSearch?.ids || []}
        initialSelectionId={null}
        singleSelect
        showAddView={false}
      />
    </div>
  );
}

export default ClientSelection;